import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { clearCheckoutDrafts } from "../lib/checkout-draft";
import { api, ApiClientError } from "../lib/api";
import { clearSession, persistSession, readStoredUser } from "../lib/session-storage";
import type { User } from "../types";

type SessionPayload = { token: string; user: User };
export type MfaChallenge = { mfaRequired: true; challengeId: string; expiresAt: string; debugCode?: string };
type AuthValue = { user: User | null; isRestoring: boolean; login: (email: string, password: string) => Promise<User | MfaChallenge>; verifyMfa: (challengeId: string, code: string) => Promise<User>; learnerLogin: (learnerCode: string, pin: string) => Promise<User | MfaChallenge>; adoptSession: (session: SessionPayload) => User; logout: () => Promise<void>; updateUser: (user: User) => void };
const AuthContext = createContext<AuthValue | null>(null);

function withAvatar(user: User): User {
  if (user.avatarUrl) return user;
  const initials = user.name.split(" ").filter(Boolean).map((part) => part[0]).join("").slice(0, 2).toUpperCase() || "K";
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 112 112"><rect width="112" height="112" rx="32" fill="#d9fff5"/><circle cx="56" cy="56" r="36" fill="#0bbf9d"/><text x="56" y="64" text-anchor="middle" font-family="Arial,sans-serif" font-size="30" font-weight="800" fill="#102332">${initials}</text></svg>`;
  return { ...user, avatarUrl: `data:image/svg+xml,${encodeURIComponent(svg)}` };
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const [user, setUser] = useState<User | null>(() => {
    const stored = localStorage.getItem("kidix_token") ? readStoredUser(localStorage) : null;
    return stored ? withAvatar(stored) : null;
  });
  const [isRestoring, setIsRestoring] = useState(() => Boolean(localStorage.getItem("kidix_token")));
  const currentAccountId = useRef(user?.id ?? null);
  currentAccountId.current = user?.id ?? null;
  const adoptSession = useCallback((session: SessionPayload) => {
    const normalizedUser = withAvatar(session.user);
    if (readStoredUser(localStorage)?.id !== normalizedUser.id) {
      void queryClient.cancelQueries();
      queryClient.clear();
      clearCheckoutDrafts(sessionStorage);
    }
    persistSession(localStorage, { ...session, user: normalizedUser });
    setUser(normalizedUser);
    return normalizedUser;
  }, [queryClient]);
  async function login(email: string, password: string) {
    const result = await api<SessionPayload | MfaChallenge>("/auth/login", { method: "POST", body: JSON.stringify({ email, password }) });
    return "mfaRequired" in result ? result : adoptSession(result);
  }
  async function verifyMfa(challengeId: string, code: string) {
    return adoptSession(await api<SessionPayload>("/auth/mfa/verify", { method: "POST", body: JSON.stringify({ challengeId, code }) }));
  }
  async function learnerLogin(learnerCode: string, pin: string) {
    const result = await api<SessionPayload | MfaChallenge>("/auth/learner-login", { method: "POST", body: JSON.stringify({ learnerCode, pin }) });
    return "mfaRequired" in result ? result : adoptSession(result);
  }
  async function logout() {
    const signOutRequest = api("/auth/logout", { method: "POST" });
    clearSession(localStorage); clearCheckoutDrafts(sessionStorage); setUser(null);
    void queryClient.cancelQueries();
    queryClient.clear();
    try { await signOutRequest; } catch { /* Local cleanup already completed. */ }
  }
  function updateUser(nextUser: User) {
    const normalizedUser = withAvatar(nextUser);
    const token = localStorage.getItem("kidix_token");
    if (token) persistSession(localStorage, { token, user: normalizedUser });
    setUser(normalizedUser);
  }
  useEffect(() => {
    let active = true;
    async function restore() {
      if (!localStorage.getItem("kidix_token")) { setIsRestoring(false); return; }
      try {
        const current = await api<User>("/auth/me");
        if (active) updateUser(current);
      } catch (error) {
        if (active && error instanceof ApiClientError && error.status === 401) { void queryClient.cancelQueries(); queryClient.clear(); clearSession(localStorage); clearCheckoutDrafts(sessionStorage); setUser(null); }
      } finally {
        if (active) setIsRestoring(false);
      }
    }
    const expired = () => { clearCheckoutDrafts(sessionStorage); void queryClient.cancelQueries(); queryClient.clear(); setUser(null); setIsRestoring(false); };
    const synchronized = (event: StorageEvent) => {
      if (event.storageArea !== localStorage || (event.key !== null && !["kidix_token", "kidix_user"].includes(event.key))) return;
      const stored = localStorage.getItem("kidix_token") ? readStoredUser(localStorage) : null;
      if (!stored || stored.id !== currentAccountId.current) {
        void queryClient.cancelQueries(); queryClient.clear(); clearCheckoutDrafts(sessionStorage);
      }
      setUser(stored ? withAvatar(stored) : null);
      setIsRestoring(false);
    };
    window.addEventListener("kidix:session-expired", expired);
    window.addEventListener("storage", synchronized);
    void restore();
    return () => { active = false; window.removeEventListener("kidix:session-expired", expired); window.removeEventListener("storage", synchronized); };
  }, [queryClient]);
  const value = useMemo(() => ({ user, isRestoring, login, verifyMfa, learnerLogin, adoptSession, logout, updateUser }), [user, isRestoring, adoptSession]);
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used inside AuthProvider");
  return context;
}
